// vg-binding: CONTEXT:decisions            (P15.D-20 rewire-no-new-ui, P15.D-07 audience-scoped fail-closed, P15.D-21 signal-only WS + optimistic mark-read + fail-closed banner, P15.D-14 render-at-read)
// vg-binding: INTERFACE-STANDARDS:error-shape
//
// apps/vendor/src/pages/Notifications.tsx
//
// Phase 7 Wave 13 Task 46 — notifications full page with 6 filter tabs.
//
// Phase 15 POST-REVIEW FIX FF (F16): MIGRATED off the ARCHIVED
// `/api/v1/vendor/notifications` surface (the bell was already re-wired in Task
// 24; this page was the last reader) onto the CANONICAL, audience-scoped
// `/api/v1/notifications` surface via `features/notifications/canonical.ts` —
// the SAME client the bell uses. The audience is derived server-side from the
// portal JWT (P15.D-07), so this page shows ONLY vendor-audience rows and never
// leaks merchant notifications.
//
// Rewire notes (P15.D-20 — no new design):
//   - list feed  → GET /api/v1/notifications (cursor-accumulated "Load more").
//     The canonical read layer is audience-scoped but NOT category-filtered, so
//     the tab filter (orders/products/payments/system) + the "unread" tab are
//     applied CLIENT-SIDE over the accumulated feed — identical bucketing to the
//     bell (`deriveNotificationCategory`).
//   - badge      → GET /api/v1/notifications/unread-count (fail-closed).
//   - mark-read  → PATCH /api/v1/notifications/:id/read (optimistic + rollback).
//   - read-all   → POST  /api/v1/notifications/read-all  (optimistic + rollback).
//   - title/body arrive PRE-RENDERED at the caller locale (P15.D-14) → verbatim.
//
// Fail-closed UX (P15.D-07 / D-21):
//   - 403 AUDIENCE_FORBIDDEN → banner + retry (NOT an empty state). 403-semantic
//     (post-review FA): 403 is now reserved for a genuine no-identity token; an
//     ORG-revoked vendor gets 200 with their personal feed instead, so a revoked
//     member simply sees their (possibly empty) personal feed here, never a stuck
//     error.
//   - authorized + zero rows → real empty state.
//   - both mutations carry an onError toast (require-mutation-on-error ratchet).

import { useMemo, useState } from 'react';
import type { JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Button, cn, PageHeader, useToast } from '@printway/ui';

import {
  deriveNotificationCategory,
  isAudienceForbidden,
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotificationFeedPages,
  useUnreadCount,
  type CanonicalNotification,
} from '../features/notifications/canonical.js';

type TabKey = 'all' | 'unread' | 'orders' | 'products' | 'payments' | 'system';

const TAB_KEYS: ReadonlyArray<TabKey> = [
  'all',
  'unread',
  'orders',
  'products',
  'payments',
  'system',
];

const NOTIF_PAGE_LIMIT = 20;

function formatTs(iso: string, locale: string): string {
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return '';
  // Locale tag comes from the active i18n language (a variable) — never a
  // hardcoded 'vi-VN'/'en-US'/'fr-FR' literal (CLAUDE.md i18n locale rule).
  return new Date(ts).toLocaleString(locale, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

export function NotificationsPage(): JSX.Element {
  const { t, i18n } = useTranslation('orders');
  const { toast } = useToast();
  const navigate = useNavigate();
  const [tab, setTab] = useState<TabKey>('all');

  const feedQuery = useNotificationFeedPages({ limit: NOTIF_PAGE_LIMIT });
  const unreadQuery = useUnreadCount();
  const markM = useMarkNotificationRead();
  const markAllM = useMarkAllNotificationsRead();

  // Fail-closed badge (P15.D-07 / D-21): only trust the count on a SUCCESSFUL
  // fetch; a 403/pending/error state shows nothing rather than a stale number.
  const unread =
    unreadQuery.isSuccess && typeof unreadQuery.data === 'number'
      ? unreadQuery.data
      : 0;

  const feedForbidden = isAudienceForbidden(feedQuery.error);
  const feedGenericError = feedQuery.isError && !feedForbidden;

  const items = useMemo<CanonicalNotification[]>(
    () => (feedQuery.data?.pages ?? []).flatMap((p) => p.items),
    [feedQuery.data],
  );

  // Client-side tab filter (P15.D-20 — the canonical read layer has no server
  // category param). `unread` filters the per-user read receipt; category tabs
  // bucket the canonical `type` (event_type ?? category) exactly like the bell.
  const filtered = useMemo<CanonicalNotification[]>(() => {
    if (tab === 'all') return items;
    if (tab === 'unread') return items.filter((n) => !n.read);
    return items.filter((n) => deriveNotificationCategory(n.type) === tab);
  }, [items, tab]);

  const handleMarkRead = (id: string): void => {
    markM.mutate(id, {
      onError: () => {
        toast({
          variant: 'error',
          title: t('notifications.toast.markReadFailed'),
        });
      },
    });
  };

  const handleMarkAll = (): void => {
    markAllM.mutate(undefined, {
      onError: () => {
        toast({
          variant: 'error',
          title: t('notifications.toast.markAllFailed'),
        });
      },
    });
  };

  // Clicking a row: mark-as-read + SPA navigate to the linked entity, appending
  // ?focus=<id> so the destination can scroll to it (parity with the bell).
  const handleActivate = (n: CanonicalNotification): void => {
    if (!n.read) handleMarkRead(n.id);
    const link = n.link;
    if (typeof link !== 'string' || link.length === 0) return;
    const target = link.includes('?focus=')
      ? link
      : `${link}${link.includes('?') ? '&' : '?'}focus=${encodeURIComponent(n.id)}`;
    navigate(target);
  };

  const isInitialLoading = feedQuery.isLoading;
  const showEmpty = !isInitialLoading && !feedForbidden && filtered.length === 0;

  return (
    <main
      data-testid="notifications-page-root"
      className="flex flex-col gap-4 px-6 py-6"
    >
      <PageHeader
        title={
          <>
            {t('notifications.page.title')}
            {unread > 0 ? (
              <span className="ml-2 rounded-full bg-pw-primary px-2 py-0.5 text-xs font-bold text-white">
                {unread > 99 ? '99+' : unread}
              </span>
            ) : null}
          </>
        }
        actions={
          <button
            type="button"
            data-testid="notifications-mark-all-read"
            onClick={handleMarkAll}
            disabled={markAllM.isPending || unread === 0}
            className="rounded-md border border-pw-border bg-pw-surface px-3 py-1.5 text-sm font-medium text-pw-text disabled:cursor-not-allowed disabled:opacity-50"
          >
            {t('notifications.page.markAllRead')}
          </button>
        }
      />

      <nav className="flex w-full items-center gap-1 overflow-x-auto border-b border-pw-border">
        {TAB_KEYS.map((key) => (
          <button
            key={key}
            type="button"
            data-testid={`notif-tab-${key}`}
            onClick={() => setTab(key)}
            className={cn(
              'shrink-0 border-b-2 px-3 py-2 text-sm font-medium transition-colors',
              key === tab
                ? 'border-pw-primary text-pw-primary-strong'
                : 'border-transparent text-pw-text-muted hover:text-pw-text',
            )}
          >
            {t(`notifications.filter.${key}`)}
          </button>
        ))}
      </nav>

      {feedForbidden ? (
        // 2-case revoke (P15.D-21): audience-block → banner + retry, NOT an empty
        // state. Testids follow the cross-portal convention.
        <div
          role="alert"
          data-testid="notif-forbidden-banner"
          className="flex flex-col items-center gap-2 rounded-md border border-dashed border-pw-border bg-pw-surface px-3 py-8 text-center"
        >
          <p className="text-sm font-medium text-pw-text">
            {t('notifications.revoke.title')}
          </p>
          <p className="text-xs text-pw-text-muted">
            {t('notifications.revoke.body')}
          </p>
          <Button
            variant="secondary"
            size="sm"
            data-testid="notif-retry-btn"
            onClick={() => {
              void feedQuery.refetch();
              void unreadQuery.refetch();
            }}
            disabled={feedQuery.isFetching}
          >
            {t('notifications.revoke.retry')}
          </Button>
        </div>
      ) : feedGenericError ? (
        <p
          role="alert"
          data-testid="notifications-list-error"
          className="rounded-md border border-red-200 bg-red-50 px-3 py-6 text-center text-sm text-red-700"
        >
          {t('notifications.errorLoad', {
            code: (feedQuery.error as Error)?.message ?? 'ERROR',
          })}
        </p>
      ) : isInitialLoading ? (
        <div className="flex flex-col gap-2 py-6">
          {Array.from({ length: 6 }).map((_v, i) => (
            <div
              key={i}
              className="h-14 animate-pulse rounded-md border border-pw-border bg-pw-surface"
            />
          ))}
        </div>
      ) : showEmpty ? (
        <p className="rounded-md border border-dashed border-pw-border bg-pw-surface px-3 py-8 text-center text-sm text-pw-text-muted">
          {t('notifications.page.emptyTab')}
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {filtered.map((n) => (
            <li
              key={n.id}
              data-testid={`notif-row-${n.id}`}
              className={cn(
                'flex items-start justify-between gap-3 rounded-md border border-pw-border bg-pw-bg p-3 transition-colors',
                !n.read && 'border-pw-primary/30 bg-pw-primary/5',
                n.link ? 'cursor-pointer hover:bg-pw-surface' : '',
              )}
              onClick={n.link ? () => handleActivate(n) : undefined}
              role={n.link ? 'button' : undefined}
              tabIndex={n.link ? 0 : undefined}
              onKeyDown={
                n.link
                  ? (e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleActivate(n);
                      }
                    }
                  : undefined
              }
            >
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-pw-text">
                  {!n.read ? (
                    <span className="mr-2 inline-block h-2 w-2 rounded-full bg-pw-primary" />
                  ) : null}
                  {n.title}
                </p>
                {n.body ? (
                  <p className="mt-1 text-sm text-pw-text-muted">{n.body}</p>
                ) : null}
                <p className="mt-1 text-xs text-pw-text-muted">
                  {formatTs(n.createdAt, i18n.language)} · {n.type}
                </p>
                {n.link ? (
                  <span className="mt-1 inline-block text-xs font-semibold text-pw-primary">
                    {t('notifications.page.viewDetail')}
                  </span>
                ) : null}
              </div>
              {!n.read ? (
                <button
                  type="button"
                  data-testid={`notif-mark-read-${n.id}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMarkRead(n.id);
                  }}
                  className="rounded-md border border-pw-border bg-pw-bg px-2 py-1 text-xs font-medium text-pw-text-muted hover:bg-pw-surface"
                >
                  {t('notifications.page.markRead')}
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {!feedForbidden && !feedGenericError && feedQuery.hasNextPage ? (
        <div className="flex justify-center pt-2">
          <Button
            variant="secondary"
            size="sm"
            data-testid="notifications-loadmore"
            onClick={() => void feedQuery.fetchNextPage()}
            disabled={feedQuery.isFetchingNextPage}
          >
            {feedQuery.isFetchingNextPage
              ? t('notifications.page.loading')
              : t('notifications.page.loadMore')}
          </Button>
        </div>
      ) : null}
    </main>
  );
}

export default NotificationsPage;
